// frontend/src/manager_notifications/useNotifications.js

import { useState, useEffect, useCallback } from 'react';
import { notificationManager } from './notificationManager';

export const useNotifications = (options = {}) => {
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState(options.filters || {});
  
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const all = await notificationManager.getAllNotifications({ forceRefresh: options.forceRefresh });
      setNotifications(all);
      
      const filtered = await notificationManager.filterNotifications(filters);
      setFilteredNotifications(filtered);
      
      const statsData = await notificationManager.getStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, options.forceRefresh]);
  
  useEffect(() => {
    loadData();
    
    const unsubscribe = notificationManager.subscribe(loadData);
    
    const interval = setInterval(loadData, options.refreshInterval || 30000);
    
    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [loadData, options.refreshInterval]);
  
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);
  
  const markAsRead = useCallback((notificationId) => {
    notificationManager.markAsRead(notificationId);
    loadData();
  }, [loadData]);
  
  const markAllAsRead = useCallback(() => {
    notificationManager.markAllAsRead();
    loadData();
  }, [loadData]);
  
  const refresh = useCallback(() => {
    loadData();
  }, [loadData]);
  
  return {
    notifications,
    filteredNotifications,
    loading,
    stats,
    filters,
    updateFilters,
    markAsRead,
    markAllAsRead,
    refresh,
    unreadCount: notificationManager.getUnreadCount()
  };
};

export const useUpcomingNotifications = (limit = 10) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const upcoming = await notificationManager.getUpcomingNotifications(limit);
      setNotifications(upcoming);
    } catch (error) {
      console.error('Error loading upcoming notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [limit]);
  
  useEffect(() => {
    loadData();
    
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [loadData]);
  
  return { notifications, loading, refresh: loadData };
};

export const useUrgentNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const urgent = await notificationManager.getUrgentNotifications();
      setNotifications(urgent);
    } catch (error) {
      console.error('Error loading urgent notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadData();
    
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, [loadData]);
  
  return { notifications, loading, refresh: loadData };
};